import React, { useEffect, useMemo, useState } from "react";
import AppSectionCard from "./AppSectionCard";
import { pageStyles } from "../styles/appTheme";

const API_BASE = import.meta.env.VITE_API_BASE || "";

type LibrarianStatus = {
  mode?: string;
  automatic_downloads?: boolean;
  installation_enabled?: boolean;
  activation_enabled?: boolean;
  assistant_can_approve?: boolean;
  explicit_admin_review_required?: boolean;
  manifest_verification_enabled?: boolean;
  manifest_fetches_pack_files?: boolean;
  sources?: {
    total?: number;
    enabled?: number;
  };
  recommendations?: {
    total?: number;
    pending?: number;
    approved?: number;
    rejected?: number;
  };
  manifest_verification?: {
    verified?: number;
    failed?: number;
  };
  capability_gap_ranking_enabled?: boolean;
  capability_gaps_automatically_inferred?: boolean;
  ranking_automatically_selects?: boolean;
  ranking_automatically_approves?: boolean;
  ranking_downloads_pack_files?: boolean;
  capability_gaps?: {
    total?: number;
    open?: number;
    resolved?: number;
    dismissed?: number;
  };
};

type SigningKeyState = {
  configured?: boolean;
  key_id?: string | null;
  public_key_fingerprint_sha256?: string | null;
  pinned_by?: string | null;
  pinned_at?: string | null;
  updated_at?: string | null;
};

type ManifestVerificationState = {
  status?: "not_verified" | "verified" | "failed" | "stale";
  verification_id?: string | null;
  verified_at?: string | null;
  verified_by?: string | null;
  manifest_sha256?: string | null;
  key_id?: string | null;
  public_key_fingerprint_sha256?: string | null;
  file_count?: number | null;
  total_file_bytes?: number | null;
  error?: string | null;
  stale_reasons?: string[];
  details?: Record<string, unknown>;
};

type LibrarianSource = {
  source_id: string;
  name: string;
  publisher: string;
  base_url: string;
  hostname: string;
  enabled: boolean;
  signing_key?: SigningKeyState;
};

type LibrarianRecommendation = {
  recommendation_id: string;
  source_id: string;
  source_name?: string;
  source_hostname?: string;
  source_enabled?: boolean;
  title: string;
  description?: string;
  publisher: string;
  license_name: string;
  version: string;
  size_bytes: number;
  source_updated_at?: string | null;
  manifest_path: string;
  sha256: string;
  signature_status: string;
  recommendation_reason: string;
  provenance?: Record<string, unknown>;
  status: "pending" | "approved" | "rejected";
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_note?: string | null;
  signing_key?: SigningKeyState;
  manifest_verification?: ManifestVerificationState;
};

type CapabilityGap = {
  gap_id: string;
  title: string;
  description: string;
  required_terms: string[];
  preferred_terms: string[];
  allowed_licenses: string[];
  max_size_bytes?: number | null;
  priority: "low" | "normal" | "high" | "critical";
  status: "open" | "resolved" | "dismissed";
  origin: "explicit_admin_definition";
  automatically_inferred: false;
  created_by: string;
  created_at: string;
  updated_at: string;
  closed_by?: string | null;
  closed_at?: string | null;
  resolution_note?: string | null;
  fingerprint_sha256: string;
};

type RankedCandidate = {
  rank: number;
  recommendation_id: string;
  eligible: boolean;
  score: number;
  score_breakdown: {
    relevance: number;
    trust: number;
    license: number;
    efficiency: number;
    freshness: number;
    compatibility: number | null;
  };
  matched_required_terms: string[];
  missing_required_terms: string[];
  matched_preferred_terms: string[];
  eligibility_reasons: string[];
  explanation: string[];
  candidate_size_bytes: number;
  compatibility_status: string;
  automatically_selected: false;
  automatically_approved: false;
  downloaded: false;
  installed: false;
  activated: false;
  executed: false;
  candidate: {
    recommendation_id?: string;
    source_name?: string;
    source_hostname?: string;
    source_enabled?: boolean;
    title?: string;
    description?: string;
    publisher?: string;
    license_name?: string;
    version?: string;
    size_bytes?: number;
    status?: string;
    signature_status?: string;
    manifest_verification?: ManifestVerificationState;
  };
};

type GapRanking = {
  ok: boolean;
  gap: CapabilityGap;
  ranking_policy: string;
  candidate_count: number;
  eligible_count: number;
  ineligible_count: number;
  candidates: RankedCandidate[];
  highest_scoring_eligible_recommendation_id?: string | null;
  automatically_inferred: false;
  automatically_selected: false;
  automatically_approved: false;
  metadata_only: true;
  downloaded: false;
  installed: false;
  activated: false;
  executed: false;
};

type Props = {
  isElevated: boolean;
};

function getCookie(name: string): string {
  if (typeof document === "undefined") return "";

  const safe = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${safe}=([^;]*)`)
  );

  return match ? decodeURIComponent(match[1]) : "";
}

function getToken(): string {
  const keys = [
    "ava_session_token",
    "ava_token",
    "token",
    "access_token",
  ];

  for (const key of keys) {
    const value =
      localStorage.getItem(key) ||
      sessionStorage.getItem(key) ||
      getCookie(key);

    if (value) return value;
  }

  return "";
}

function formatBytes(value: unknown): string {
  let amount = Math.max(0, Number(value || 0));

  if (!Number.isFinite(amount) || amount === 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  let unit = 0;

  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }

  const precision = amount >= 10 || unit === 0 ? 0 : 1;
  return `${amount.toFixed(precision)} ${units[unit]}`;
}

function detailFrom(data: any, fallback: string): string {
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  return fallback;
}

export default function LocalLibrarianPanel({
  isElevated,
}: Props) {
  const token = useMemo(() => getToken(), []);

  const headers = useMemo<Record<string, string>>(
    () => ({
      "Content-Type": "application/json",
      ...(token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {}),
    }),
    [token]
  );

  const [status, setStatus] =
    useState<LibrarianStatus | null>(null);
  const [sources, setSources] =
    useState<LibrarianSource[]>([]);
  const [recommendations, setRecommendations] =
    useState<LibrarianRecommendation[]>([]);
  const [gaps, setGaps] = useState<CapabilityGap[]>([]);
  const [rankingsByGapId, setRankingsByGapId] =
    useState<Record<string, GapRanking>>({});

  const [gapTitle, setGapTitle] = useState("");
  const [gapDescription, setGapDescription] = useState("");
  const [gapRequiredTerms, setGapRequiredTerms] = useState("");
  const [gapPreferredTerms, setGapPreferredTerms] = useState("");
  const [gapAllowedLicenses, setGapAllowedLicenses] =
    useState("");
  const [gapMaxSizeMb, setGapMaxSizeMb] = useState("");
  const [gapPriority, setGapPriority] =
    useState<CapabilityGap["priority"]>("normal");

  const [sourceName, setSourceName] = useState("");
  const [publisher, setPublisher] = useState("");
  const [baseUrl, setBaseUrl] = useState("");

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [warning, setWarning] = useState("");

  async function safeJson(response: Response): Promise<any> {
    return response.json().catch(() => ({}));
  }

  async function loadLibrarian(): Promise<void> {
    if (!isElevated) return;

    setWarning("");

    try {
      const results = await Promise.allSettled([
        fetch(`${API_BASE}/api/v1/admin/librarian/status`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API_BASE}/api/v1/admin/librarian/sources`, {
          credentials: "include",
          headers,
        }),
        fetch(
          `${API_BASE}/api/v1/admin/librarian/recommendations`,
          {
            credentials: "include",
            headers,
          }
        ),
        fetch(`${API_BASE}/api/v1/admin/librarian/gaps`, {
          credentials: "include",
          headers,
        }),
      ]);

      const warnings: string[] = [];

      if (results[0].status === "fulfilled") {
        const data = await safeJson(results[0].value);

        if (results[0].value.ok) {
          setStatus(data);
        } else {
          warnings.push(
            detailFrom(data, "Unable to load Librarian status.")
          );
        }
      } else {
        warnings.push("Librarian status request failed.");
      }

      if (results[1].status === "fulfilled") {
        const data = await safeJson(results[1].value);

        if (results[1].value.ok) {
          setSources(
            Array.isArray(data?.sources) ? data.sources : []
          );
        } else {
          warnings.push(
            detailFrom(data, "Unable to load approved sources.")
          );
        }
      } else {
        warnings.push("Approved sources request failed.");
      }

      if (results[2].status === "fulfilled") {
        const data = await safeJson(results[2].value);

        if (results[2].value.ok) {
          setRecommendations(
            Array.isArray(data?.recommendations)
              ? data.recommendations
              : []
          );
        } else {
          warnings.push(
            detailFrom(
              data,
              "Unable to load knowledge recommendations."
            )
          );
        }
      } else {
        warnings.push("Recommendations request failed.");
      }

      if (results[3].status === "fulfilled") {
        const data = await safeJson(results[3].value);

        if (results[3].value.ok) {
          setGaps(
            Array.isArray(data?.gaps) ? data.gaps : []
          );
        } else {
          warnings.push(
            detailFrom(
              data,
              "Unable to load capability gaps."
            )
          );
        }
      } else {
        warnings.push("Capability gaps request failed.");
      }

      setWarning(warnings.join(" "));
    } catch (error: any) {
      setWarning(
        error?.message || "Local Librarian request failed."
      );
    }
  }

  useEffect(() => {
    if (!isElevated) {
      setStatus(null);
      setSources([]);
      setRecommendations([]);
      setGaps([]);
      setRankingsByGapId({});
      setMessage("");
      setWarning("");
      return;
    }

    void loadLibrarian();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isElevated]);

  async function mutate(
    path: string,
    body: Record<string, unknown>,
    successFallback: string
  ): Promise<boolean> {
    try {
      setBusy(true);
      setMessage("");
      setWarning("");

      const response = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(body),
      });

      const data = await safeJson(response);

      if (!response.ok) {
        setMessage(
          `❌ ${detailFrom(data, "Librarian request failed.")}`
        );
        return false;
      }

      setMessage(data?.message || successFallback);
      await loadLibrarian();
      return true;
    } catch (error: any) {
      setMessage(
        error?.message || "Librarian operation failed."
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  function parseList(value: string): string[] {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
  }

  async function createGap(): Promise<void> {
    const title = gapTitle.trim();
    const description = gapDescription.trim();
    const requiredTerms = parseList(gapRequiredTerms);
    const preferredTerms = parseList(gapPreferredTerms);
    const allowedLicenses = parseList(gapAllowedLicenses);

    if (!title || !description || !requiredTerms.length) {
      setMessage(
        "Enter a title, description, and at least one required capability term."
      );
      return;
    }

    let maxSizeBytes: number | null = null;
    const sizeText = gapMaxSizeMb.trim();

    if (sizeText) {
      const megabytes = Number(sizeText);

      if (!Number.isFinite(megabytes) || megabytes < 0) {
        setMessage(
          "Maximum size must be a non-negative number of megabytes."
        );
        return;
      }

      maxSizeBytes = Math.round(megabytes * 1024 * 1024);
    }

    const confirmed = window.confirm(
      "Create explicit capability gap?\n\n" +
        `${title}\n` +
        `Required terms: ${requiredTerms.join(", ")}\n\n` +
        "This records an administrator-defined need only. Ava will not " +
        "infer a gap, select a candidate, approve content, download files, " +
        "install, activate, or execute anything."
    );

    if (!confirmed) return;

    const created = await mutate(
      "/api/v1/admin/librarian/gaps",
      {
        title,
        description,
        required_terms: requiredTerms,
        preferred_terms: preferredTerms,
        allowed_licenses: allowedLicenses,
        max_size_bytes: maxSizeBytes,
        priority: gapPriority,
      },
      "Capability gap recorded. No candidate was selected."
    );

    if (created) {
      setGapTitle("");
      setGapDescription("");
      setGapRequiredTerms("");
      setGapPreferredTerms("");
      setGapAllowedLicenses("");
      setGapMaxSizeMb("");
      setGapPriority("normal");
    }
  }

  async function rankGap(gap: CapabilityGap): Promise<void> {
    const confirmed = window.confirm(
      "Rank current recommendation metadata?\n\n" +
        `${gap.title}\n\n` +
        "The ranking is advisory only. Ava will not select or approve a " +
        "candidate, fetch pack files, download, install, activate, or execute anything."
    );

    if (!confirmed) return;

    try {
      setBusy(true);
      setMessage("");
      setWarning("");

      const response = await fetch(
        `${API_BASE}/api/v1/admin/librarian/gaps/${encodeURIComponent(
          gap.gap_id
        )}/rank`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({}),
        }
      );

      const data = await safeJson(response);

      if (!response.ok) {
        setMessage(
          `❌ ${detailFrom(data, "Capability ranking failed.")}`
        );
        return;
      }

      setRankingsByGapId((current) => ({
        ...current,
        [gap.gap_id]: data as GapRanking,
      }));

      setMessage(
        data?.message ||
          "Current metadata ranked. No candidate was selected."
      );
    } catch (error: any) {
      setMessage(
        error?.message || "Capability ranking request failed."
      );
    } finally {
      setBusy(false);
    }
  }

  async function changeGapStatus(
    gap: CapabilityGap,
    action: "resolve" | "dismiss" | "reopen"
  ): Promise<void> {
    let note: string | null = null;

    if (action !== "reopen") {
      const entered = window.prompt(
        action === "resolve"
          ? "Resolution note:"
          : "Dismissal note:",
        ""
      );

      if (entered === null) return;
      note = entered.trim() || null;
    }

    const label =
      action === "reopen"
        ? "Reopen"
        : action === "resolve"
          ? "Resolve"
          : "Dismiss";

    const confirmed = window.confirm(
      `${label} capability gap?\n\n${gap.title}`
    );

    if (!confirmed) return;

    const changed = await mutate(
      `/api/v1/admin/librarian/gaps/${encodeURIComponent(
        gap.gap_id
      )}/${action}`,
      action === "reopen" ? {} : { note },
      `Capability gap ${action}d.`
    );

    if (changed) {
      setRankingsByGapId((current) => {
        const next = { ...current };
        delete next[gap.gap_id];
        return next;
      });
    }
  }

  async function registerSource(): Promise<void> {
    const cleanName = sourceName.trim();
    const cleanPublisher = publisher.trim();
    const cleanUrl = baseUrl.trim();

    if (!cleanName || !cleanPublisher || !cleanUrl) {
      setMessage(
        "Enter a source name, publisher, and HTTPS base URL."
      );
      return;
    }

    const created = await mutate(
      "/api/v1/admin/librarian/sources",
      {
        name: cleanName,
        publisher: cleanPublisher,
        base_url: cleanUrl,
      },
      "Approved source registered. Nothing was downloaded."
    );

    if (created) {
      setSourceName("");
      setPublisher("");
      setBaseUrl("");
    }
  }

  async function scanApprovedSources(): Promise<void> {
    const confirmed = window.confirm(
      "Scan all enabled approved sources?\n\n" +
        "Only the fixed, bounded JSON metadata index will be read. " +
        "No knowledge packs, files, or executables will be downloaded."
    );

    if (!confirmed) return;

    await mutate(
      "/api/v1/admin/librarian/scan",
      {},
      "Approved-source metadata scan completed. Nothing was downloaded or installed."
    );
  }

  async function toggleSource(
    source: LibrarianSource
  ): Promise<void> {
    const action = source.enabled ? "disable" : "enable";

    const confirmed = window.confirm(
      `${source.enabled ? "Disable" : "Enable"} discovery source?\n\n` +
        `${source.publisher} — ${source.name}\n` +
        `${source.base_url}\n\n` +
        "This changes discovery eligibility only."
    );

    if (!confirmed) return;

    await mutate(
      `/api/v1/admin/librarian/sources/${encodeURIComponent(
        source.source_id
      )}/${action}`,
      {},
      `Source ${action}d.`
    );
  }

  async function pinSigningKey(
    source: LibrarianSource
  ): Promise<void> {
    const keyId = window.prompt(
      "Publisher signing-key ID:",
      source.signing_key?.key_id || ""
    );

    if (keyId === null) return;

    const cleanKeyId = keyId.trim();

    if (!cleanKeyId) {
      setMessage("Enter the publisher signing-key ID.");
      return;
    }

    const publicKey = window.prompt(
      "Paste the publisher's base64 Ed25519 public key.\n\n" +
        "The key must decode to exactly 32 bytes.",
      ""
    );

    if (publicKey === null) return;

    const cleanPublicKey = publicKey.trim();

    if (!cleanPublicKey) {
      setMessage("Enter the publisher Ed25519 public key.");
      return;
    }

    const confirmed = window.confirm(
      `${source.signing_key?.configured ? "Rotate" : "Pin"} publisher signing key?\n\n` +
        `${source.publisher} — ${source.name}\n` +
        `Key ID: ${cleanKeyId}\n\n` +
        "Changing a pinned key makes earlier successful manifest " +
        "verifications stale. No knowledge pack files will be fetched."
    );

    if (!confirmed) return;

    await mutate(
      `/api/v1/admin/librarian/sources/${encodeURIComponent(
        source.source_id
      )}/signing-key`,
      {
        key_id: cleanKeyId,
        public_key_b64: cleanPublicKey,
      },
      "Publisher Ed25519 signing key pinned."
    );
  }

  async function verifyManifest(
    recommendation: LibrarianRecommendation
  ): Promise<void> {
    if (recommendation.status !== "approved") {
      setMessage(
        "Approve the recommendation before verifying its manifest."
      );
      return;
    }

    const confirmed = window.confirm(
      "Verify signed knowledge manifest?\n\n" +
        `${recommendation.title}\n` +
        `Publisher: ${recommendation.publisher}\n` +
        `Version: ${recommendation.version}\n` +
        `Manifest: ${recommendation.manifest_path}\n\n` +
        "AVAAIO will fetch only the bounded JSON manifest and verify " +
        "its identity, declared files, hashes, compatibility metadata, " +
        "and Ed25519 signature. Pack files will not be downloaded, " +
        "installed, activated, or executed."
    );

    if (!confirmed) return;

    await mutate(
      `/api/v1/admin/librarian/recommendations/${encodeURIComponent(
        recommendation.recommendation_id
      )}/verify-manifest`,
      {},
      "Signed manifest verified. No pack files were downloaded."
    );
  }

  async function reviewRecommendation(
    recommendation: LibrarianRecommendation,
    decision: "approve" | "reject"
  ): Promise<void> {
    const approving = decision === "approve";

    const confirmed = window.confirm(
      `${approving ? "Approve" : "Reject"} knowledge recommendation?\n\n` +
        `${recommendation.title}\n` +
        `Publisher: ${recommendation.publisher}\n` +
        `Version: ${recommendation.version}\n\n` +
        (approving
          ? "Approval records eligibility only. Nothing will be downloaded or installed."
          : "The recommendation will be marked rejected.")
    );

    if (!confirmed) return;

    const note = window.prompt(
      "Optional administrator review note:",
      ""
    );

    if (note === null) return;

    await mutate(
      `/api/v1/admin/librarian/recommendations/${encodeURIComponent(
        recommendation.recommendation_id
      )}/${decision}`,
      {
        note: note.trim() || null,
      },
      `Recommendation ${decision}d.`
    );
  }

  const mode = String(
    status?.mode || "discovery_only"
  ).replace(/_/g, " ");

  return (
    <AppSectionCard>
      <div style={styles.title}>
        AVAAIO Local Librarian
      </div>

      <div style={styles.note}>
        Explicit capability gaps, deterministic advisory ranking,
        approval-gated discovery, and signed-manifest verification.
        Ava cannot automatically infer needs, select candidates,
        download, install, activate, execute, or approve knowledge packs.
      </div>

      <div style={styles.badges}>
        <div style={styles.primaryBadge}>MODE: {mode}</div>

        <div style={styles.badge}>
          PENDING: {status?.recommendations?.pending ?? 0}
        </div>

        <div style={styles.badge}>
          SOURCES: {status?.sources?.enabled ?? 0}/
          {status?.sources?.total ?? 0}
        </div>

        <div style={styles.badge}>
          VERIFIED MANIFESTS:{" "}
          {status?.manifest_verification?.verified ?? 0}
        </div>

        <div style={styles.badge}>
          OPEN GAPS: {status?.capability_gaps?.open ?? 0}
        </div>
      </div>

      <div style={styles.securityBox}>
        Automatic gap inference: OFF • Automatic selection: OFF •
        Automatic approval: OFF • Automatic downloads: OFF •
        Pack-file fetching: OFF • Installation: OFF • Activation: OFF •
        Execution: OFF • Elevated administrator review: REQUIRED
      </div>

      <div style={styles.subTitle}>
        Explicit Capability Gaps
      </div>

      <div style={styles.note}>
        Define exactly what Ava is missing. Required terms are hard
        eligibility gates. Compatibility remains unscored until AVAAIO
        has a trustworthy runtime-version signal.
      </div>

      <input
        value={gapTitle}
        onChange={(event) => setGapTitle(event.target.value)}
        placeholder="Capability-gap title"
        style={styles.inputSpaced}
      />

      <textarea
        value={gapDescription}
        onChange={(event) =>
          setGapDescription(event.target.value)
        }
        placeholder="Describe the missing capability and why it is needed"
        style={styles.textarea}
      />

      <input
        value={gapRequiredTerms}
        onChange={(event) =>
          setGapRequiredTerms(event.target.value)
        }
        placeholder="Required terms, comma separated — e.g. networking, DNS"
        style={styles.inputSpaced}
      />

      <input
        value={gapPreferredTerms}
        onChange={(event) =>
          setGapPreferredTerms(event.target.value)
        }
        placeholder="Preferred terms, comma separated — optional"
        style={styles.inputSpaced}
      />

      <input
        value={gapAllowedLicenses}
        onChange={(event) =>
          setGapAllowedLicenses(event.target.value)
        }
        placeholder="Allowed licenses, comma separated — optional"
        style={styles.inputSpaced}
      />

      <div style={styles.formGrid}>
        <input
          value={gapMaxSizeMb}
          onChange={(event) =>
            setGapMaxSizeMb(event.target.value)
          }
          placeholder="Maximum size in MB — optional"
          inputMode="decimal"
          style={styles.input}
        />

        <select
          value={gapPriority}
          onChange={(event) =>
            setGapPriority(
              event.target.value as CapabilityGap["priority"]
            )
          }
          style={styles.input}
        >
          <option value="low">Low priority</option>
          <option value="normal">Normal priority</option>
          <option value="high">High priority</option>
          <option value="critical">Critical priority</option>
        </select>
      </div>

      <div style={styles.actions}>
        <button
          type="button"
          style={pageStyles.button}
          disabled={busy}
          onClick={() => void createGap()}
        >
          {busy ? "Working..." : "Create Capability Gap"}
        </button>
      </div>

      <div style={styles.gapList}>
        {gaps.length ? (
          gaps.map((gap) => {
            const ranking = rankingsByGapId[gap.gap_id];

            return (
              <div
                key={gap.gap_id}
                style={{
                  ...styles.item,
                  border:
                    gap.status === "open"
                      ? "1px solid rgba(103,232,249,0.28)"
                      : gap.status === "resolved"
                        ? "1px solid rgba(110,255,170,0.28)"
                        : "1px solid rgba(255,205,100,0.26)",
                }}
              >
                <div style={styles.itemTitle}>
                  {gap.title}
                </div>

                <div style={styles.meta}>
                  Priority: {gap.priority.toUpperCase()} • Status:{" "}
                  {gap.status.toUpperCase()} • Explicit administrator
                  definition
                </div>

                <div style={styles.note}>
                  {gap.description}
                </div>

                <div style={styles.reason}>
                  Required: {gap.required_terms.join(", ")}
                </div>

                {gap.preferred_terms.length ? (
                  <div style={styles.meta}>
                    Preferred: {gap.preferred_terms.join(", ")}
                  </div>
                ) : null}

                {gap.allowed_licenses.length ? (
                  <div style={styles.meta}>
                    Allowed licenses:{" "}
                    {gap.allowed_licenses.join(", ")}
                  </div>
                ) : null}

                <div style={styles.meta}>
                  Maximum size:{" "}
                  {gap.max_size_bytes == null
                    ? "No additional limit"
                    : formatBytes(gap.max_size_bytes)}
                </div>

                {gap.resolution_note ? (
                  <div style={styles.note}>
                    Administrator note: {gap.resolution_note}
                  </div>
                ) : null}

                <div style={styles.breakable}>
                  Gap fingerprint: {gap.fingerprint_sha256}
                </div>

                <div style={styles.actions}>
                  {gap.status === "open" ? (
                    <>
                      <button
                        type="button"
                        style={{
                          ...pageStyles.button,
                          border:
                            "1px solid rgba(103,232,249,0.48)",
                        }}
                        disabled={busy}
                        onClick={() => void rankGap(gap)}
                      >
                        Rank Current Candidates
                      </button>

                      <button
                        type="button"
                        style={pageStyles.button}
                        disabled={busy}
                        onClick={() =>
                          void changeGapStatus(gap, "resolve")
                        }
                      >
                        Mark Resolved
                      </button>

                      <button
                        type="button"
                        style={pageStyles.button}
                        disabled={busy}
                        onClick={() =>
                          void changeGapStatus(gap, "dismiss")
                        }
                      >
                        Dismiss
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      style={pageStyles.button}
                      disabled={busy}
                      onClick={() =>
                        void changeGapStatus(gap, "reopen")
                      }
                    >
                      Reopen Gap
                    </button>
                  )}
                </div>

                {ranking ? (
                  <div style={styles.rankingBox}>
                    <div style={styles.itemTitle}>
                      Advisory Candidate Ranking
                    </div>

                    <div style={styles.meta}>
                      Policy: {ranking.ranking_policy} • Candidates:{" "}
                      {ranking.candidate_count} • Eligible:{" "}
                      {ranking.eligible_count} • Ineligible:{" "}
                      {ranking.ineligible_count}
                    </div>

                    <div style={styles.securityMini}>
                      Metadata only • Not selected • Not approved •
                      Not downloaded • Not installed • Not activated •
                      Not executed
                    </div>

                    {ranking.candidates.length ? (
                      ranking.candidates.map((candidate) => (
                        <div
                          key={candidate.recommendation_id}
                          style={styles.candidate}
                        >
                          <div style={styles.itemTitle}>
                            #{candidate.rank}{" "}
                            {candidate.candidate.title ||
                              candidate.recommendation_id}
                          </div>

                          <div style={styles.score}>
                            Score: {candidate.score.toFixed(2)} / 100 •{" "}
                            {candidate.eligible
                              ? "ELIGIBLE"
                              : "INELIGIBLE"}
                          </div>

                          <div style={styles.meta}>
                            {candidate.candidate.publisher ||
                              "Unknown publisher"}{" "}
                            • Version{" "}
                            {candidate.candidate.version || "unknown"} •{" "}
                            {formatBytes(
                              candidate.candidate.size_bytes
                            )}
                          </div>

                          <div style={styles.meta}>
                            Relevance:{" "}
                            {candidate.score_breakdown.relevance} • Trust:{" "}
                            {candidate.score_breakdown.trust} • License:{" "}
                            {candidate.score_breakdown.license} •
                            Efficiency:{" "}
                            {candidate.score_breakdown.efficiency} •
                            Freshness:{" "}
                            {candidate.score_breakdown.freshness} •
                            Compatibility: NOT SCORED
                          </div>

                          {candidate.matched_required_terms.length ? (
                            <div style={styles.meta}>
                              Matched required terms:{" "}
                              {candidate.matched_required_terms.join(
                                ", "
                              )}
                            </div>
                          ) : null}

                          {candidate.missing_required_terms.length ? (
                            <div style={styles.warningInline}>
                              Missing required terms:{" "}
                              {candidate.missing_required_terms.join(
                                ", "
                              )}
                            </div>
                          ) : null}

                          {candidate.eligibility_reasons.length ? (
                            <div style={styles.warningInline}>
                              Ineligible because:{" "}
                              {candidate.eligibility_reasons
                                .map((reason) =>
                                  reason.replace(/_/g, " ")
                                )
                                .join(", ")}
                            </div>
                          ) : null}

                          {candidate.explanation.length ? (
                            <div style={styles.note}>
                              {candidate.explanation.join(" ")}
                            </div>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <div style={styles.note}>
                        No current recommendation metadata is available
                        to rank.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })
        ) : (
          <div style={styles.note}>
            No capability gaps have been explicitly defined.
          </div>
        )}
      </div>

      <div style={styles.subTitle}>
        Register Approved Source
      </div>

      <input
        value={sourceName}
        onChange={(event) =>
          setSourceName(event.target.value)
        }
        placeholder="Source name"
        style={styles.input}
      />

      <input
        value={publisher}
        onChange={(event) =>
          setPublisher(event.target.value)
        }
        placeholder="Publisher"
        style={styles.inputSpaced}
      />

      <input
        value={baseUrl}
        onChange={(event) =>
          setBaseUrl(event.target.value)
        }
        placeholder="https://trusted-publisher.example/packs"
        inputMode="url"
        style={styles.inputSpaced}
      />

      <div style={styles.actions}>
        <button
          type="button"
          style={pageStyles.button}
          disabled={busy}
          onClick={() => void registerSource()}
        >
          {busy ? "Saving..." : "Register Approved Source"}
        </button>

        <button
          type="button"
          style={{
            ...pageStyles.button,
            border: "1px solid rgba(110,255,170,0.52)",
          }}
          disabled={busy || !sources.some((source) => source.enabled)}
          onClick={() => void scanApprovedSources()}
          title={
            sources.some((source) => source.enabled)
              ? "Scan metadata from enabled approved sources"
              : "Register and enable an approved source first"
          }
        >
          {busy ? "Working..." : "Scan Approved Sources"}
        </button>

        <button
          type="button"
          style={pageStyles.button}
          disabled={busy}
          onClick={() => void loadLibrarian()}
        >
          Refresh Librarian
        </button>
      </div>

      <div style={styles.subTitle}>Approved Sources</div>

      <div style={styles.list}>
        {sources.length ? (
          sources.map((source) => (
            <div key={source.source_id} style={styles.item}>
              <div style={styles.itemTitle}>
                {source.publisher} — {source.name}
              </div>

              <div style={styles.meta}>
                {source.hostname} •{" "}
                {source.enabled ? "ENABLED" : "DISABLED"}
              </div>

              <div style={styles.breakable}>
                {source.base_url}
              </div>

              <div style={styles.meta}>
                Signing key:{" "}
                {source.signing_key?.configured
                  ? source.signing_key.key_id || "PINNED"
                  : "NOT PINNED"}
              </div>

              {source.signing_key
                ?.public_key_fingerprint_sha256 ? (
                <div style={styles.breakable}>
                  Ed25519 fingerprint:{" "}
                  {
                    source.signing_key
                      .public_key_fingerprint_sha256
                  }
                </div>
              ) : null}

              <div style={styles.actions}>
                <button
                  type="button"
                  style={pageStyles.button}
                  disabled={busy}
                  onClick={() => void toggleSource(source)}
                >
                  {source.enabled
                    ? "Disable Discovery"
                    : "Enable Discovery"}
                </button>

                <button
                  type="button"
                  style={pageStyles.button}
                  disabled={busy}
                  onClick={() => void pinSigningKey(source)}
                >
                  {source.signing_key?.configured
                    ? "Rotate Signing Key"
                    : "Pin Signing Key"}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.note}>
            No approved sources have been registered.
          </div>
        )}
      </div>

      <div style={styles.subTitle}>
        Knowledge Recommendations
      </div>

      <div style={styles.recommendationList}>
        {recommendations.length ? (
          recommendations.map((recommendation) => (
            <div
              key={recommendation.recommendation_id}
              style={{
                ...styles.item,
                border:
                  recommendation.status === "approved"
                    ? "1px solid rgba(110,255,170,0.32)"
                    : recommendation.status === "rejected"
                      ? "1px solid rgba(255,140,140,0.32)"
                      : "1px solid rgba(103,232,249,0.20)",
              }}
            >
              <div style={styles.itemTitle}>
                {recommendation.title}
              </div>

              <div style={styles.meta}>
                {recommendation.publisher} • Version{" "}
                {recommendation.version} •{" "}
                {formatBytes(recommendation.size_bytes)}
              </div>

              <div style={styles.meta}>
                License: {recommendation.license_name} • Signature:{" "}
                {recommendation.signature_status} • Status:{" "}
                {recommendation.status.toUpperCase()}
              </div>

              <div style={styles.note}>
                {recommendation.description ||
                  "No description provided."}
              </div>

              <div style={styles.reason}>
                Why Ava recommends it:{" "}
                {recommendation.recommendation_reason}
              </div>

              <div style={styles.breakable}>
                Source:{" "}
                {recommendation.source_name ||
                  recommendation.source_hostname ||
                  recommendation.source_id}
                <br />
                Manifest: {recommendation.manifest_path}
                <br />
                Package SHA-256: {recommendation.sha256}
              </div>

              <div
                style={{
                  ...styles.verificationBox,
                  border:
                    recommendation.manifest_verification?.status ===
                    "verified"
                      ? "1px solid rgba(110,255,170,0.32)"
                      : recommendation.manifest_verification
                            ?.status === "failed"
                        ? "1px solid rgba(255,120,120,0.34)"
                        : recommendation.manifest_verification
                              ?.status === "stale"
                          ? "1px solid rgba(255,205,100,0.34)"
                          : "1px solid rgba(103,232,249,0.18)",
                }}
              >
                <div style={styles.meta}>
                  Manifest verification:{" "}
                  {(
                    recommendation.manifest_verification?.status ||
                    "not_verified"
                  )
                    .replace(/_/g, " ")
                    .toUpperCase()}
                </div>

                <div style={styles.meta}>
                  Signing key:{" "}
                  {recommendation.signing_key?.configured
                    ? recommendation.signing_key.key_id ||
                      "PINNED"
                    : "NOT PINNED"}
                </div>

                {recommendation.manifest_verification
                  ?.manifest_sha256 ? (
                  <div style={styles.breakable}>
                    Manifest SHA-256:{" "}
                    {
                      recommendation.manifest_verification
                        .manifest_sha256
                    }
                  </div>
                ) : null}

                {recommendation.manifest_verification
                  ?.file_count != null ? (
                  <div style={styles.meta}>
                    Declared files:{" "}
                    {
                      recommendation.manifest_verification
                        .file_count
                    }{" "}
                    • Declared bytes:{" "}
                    {formatBytes(
                      recommendation.manifest_verification
                        .total_file_bytes
                    )}
                  </div>
                ) : null}

                {recommendation.manifest_verification
                  ?.verified_at ? (
                  <div style={styles.meta}>
                    Last checked:{" "}
                    {
                      recommendation.manifest_verification
                        .verified_at
                    }
                  </div>
                ) : null}

                {recommendation.manifest_verification
                  ?.stale_reasons?.length ? (
                  <div style={styles.note}>
                    Stale because:{" "}
                    {recommendation.manifest_verification.stale_reasons
                      .map((reason) =>
                        reason.replace(/_/g, " ")
                      )
                      .join(", ")}
                  </div>
                ) : null}

                {recommendation.manifest_verification?.error ? (
                  <div style={styles.note}>
                    Verification error:{" "}
                    {
                      recommendation.manifest_verification
                        .error
                    }
                  </div>
                ) : null}
              </div>

              {recommendation.source_updated_at ? (
                <div style={styles.meta}>
                  Publisher updated:{" "}
                  {recommendation.source_updated_at}
                </div>
              ) : null}

              {recommendation.review_note ? (
                <div style={styles.note}>
                  Review note: {recommendation.review_note}
                </div>
              ) : null}

              {recommendation.provenance &&
              Object.keys(recommendation.provenance).length ? (
                <details style={styles.provenance}>
                  <summary>Provenance details</summary>
                  <pre style={styles.pre}>
                    {JSON.stringify(
                      recommendation.provenance,
                      null,
                      2
                    )}
                  </pre>
                </details>
              ) : null}

              {recommendation.status === "pending" ? (
                <div style={styles.actions}>
                  <button
                    type="button"
                    style={pageStyles.button}
                    disabled={busy}
                    onClick={() =>
                      void reviewRecommendation(
                        recommendation,
                        "approve"
                      )
                    }
                  >
                    Approve Eligibility
                  </button>

                  <button
                    type="button"
                    style={pageStyles.button}
                    disabled={busy}
                    onClick={() =>
                      void reviewRecommendation(
                        recommendation,
                        "reject"
                      )
                    }
                  >
                    Reject
                  </button>
                </div>
              ) : null}

              {recommendation.status === "approved" ? (
                <div style={styles.actions}>
                  <button
                    type="button"
                    style={{
                      ...pageStyles.button,
                      border:
                        "1px solid rgba(110,255,170,0.52)",
                    }}
                    disabled={
                      busy ||
                      recommendation.source_enabled === false ||
                      !recommendation.signing_key?.configured
                    }
                    onClick={() =>
                      void verifyManifest(recommendation)
                    }
                    title={
                      recommendation.source_enabled === false
                        ? "Enable the approved source first"
                        : !recommendation.signing_key
                              ?.configured
                          ? "Pin the publisher signing key first"
                          : "Fetch and verify only the signed JSON manifest"
                    }
                  >
                    {recommendation.manifest_verification
                      ?.status === "verified"
                      ? "Verify Manifest Again"
                      : "Verify Signed Manifest"}
                  </button>
                </div>
              ) : null}
            </div>
          ))
        ) : (
          <div style={styles.note}>
            No knowledge recommendations are waiting for review.
          </div>
        )}
      </div>

      {message ? <div style={styles.message}>{message}</div> : null}
      {warning ? <div style={styles.warning}>{warning}</div> : null}
    </AppSectionCard>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: {
    fontSize: "18px",
    fontWeight: 950,
    color: "#bffcff",
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    marginBottom: "14px",
  },
  subTitle: {
    marginTop: "22px",
    marginBottom: "12px",
    color: "#67f7ff",
    fontSize: "13px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  note: {
    color: "rgba(235,252,255,0.82)",
    lineHeight: 1.65,
    fontSize: "14px",
    marginTop: "8px",
  },
  badges: {
    display: "flex",
    gap: "9px",
    flexWrap: "wrap",
    margin: "16px 0 12px",
  },
  primaryBadge: {
    padding: "9px 12px",
    borderRadius: "13px",
    background: "rgba(34,211,238,0.18)",
    border: "1px solid rgba(103,232,249,0.34)",
    fontSize: "12px",
    fontWeight: 900,
    textTransform: "uppercase",
  },
  badge: {
    padding: "9px 12px",
    borderRadius: "13px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    fontSize: "12px",
    fontWeight: 850,
  },
  securityBox: {
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid rgba(110,255,170,0.28)",
    background: "rgba(20,84,55,0.18)",
    color: "#cffff0",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  input: {
    width: "100%",
    minHeight: "50px",
    boxSizing: "border-box",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.18)",
    background: "rgba(3,10,20,0.86)",
    color: "#fff",
    padding: "0 15px",
    fontSize: "14px",
  },
  inputSpaced: {
    width: "100%",
    minHeight: "50px",
    boxSizing: "border-box",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.18)",
    background: "rgba(3,10,20,0.86)",
    color: "#fff",
    padding: "0 15px",
    fontSize: "14px",
    marginTop: "10px",
  },
  textarea: {
    width: "100%",
    minHeight: "110px",
    boxSizing: "border-box",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.18)",
    background: "rgba(3,10,20,0.86)",
    color: "#fff",
    padding: "14px 15px",
    fontSize: "14px",
    lineHeight: 1.55,
    marginTop: "10px",
    resize: "vertical",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "10px",
    marginTop: "10px",
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginTop: "12px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  recommendationList: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
    maxHeight: "540px",
    overflowY: "auto",
  },
  gapList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "760px",
    overflowY: "auto",
    marginTop: "14px",
  },
  rankingBox: {
    marginTop: "13px",
    padding: "12px",
    borderRadius: "13px",
    border: "1px solid rgba(103,232,249,0.24)",
    background: "rgba(2,12,20,0.62)",
  },
  candidate: {
    marginTop: "10px",
    padding: "11px",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.035)",
  },
  score: {
    marginTop: "6px",
    color: "#bffcff",
    fontSize: "13px",
    fontWeight: 850,
  },
  securityMini: {
    marginTop: "9px",
    padding: "8px 10px",
    borderRadius: "10px",
    border: "1px solid rgba(110,255,170,0.22)",
    color: "#cffff0",
    background: "rgba(20,84,55,0.13)",
    fontSize: "11px",
    lineHeight: 1.5,
  },
  warningInline: {
    marginTop: "7px",
    color: "#ffcf9d",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  item: {
    display: "flex",
    flexDirection: "column",
    padding: "13px",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.12)",
    background: "rgba(255,255,255,0.035)",
  },
  itemTitle: {
    color: "#fff",
    fontSize: "14px",
    fontWeight: 750,
  },
  meta: {
    marginTop: "5px",
    color: "rgba(255,255,255,0.7)",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  breakable: {
    marginTop: "7px",
    color: "rgba(255,255,255,0.68)",
    fontSize: "12px",
    lineHeight: 1.55,
    wordBreak: "break-all",
  },
  reason: {
    marginTop: "9px",
    color: "#bffcff",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  verificationBox: {
    marginTop: "11px",
    padding: "10px 12px",
    borderRadius: "12px",
    background: "rgba(2,12,20,0.54)",
  },
  provenance: {
    marginTop: "10px",
    color: "#bffcff",
    fontSize: "12px",
  },
  pre: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "rgba(255,255,255,0.76)",
    fontSize: "11px",
    lineHeight: 1.5,
  },
  message: {
    marginTop: "14px",
    padding: "11px 13px",
    borderRadius: "13px",
    background: "rgba(34,211,238,0.07)",
    border: "1px solid rgba(103,232,249,0.18)",
    color: "#bffcff",
  },
  warning: {
    marginTop: "14px",
    padding: "11px 13px",
    borderRadius: "13px",
    background: "rgba(255,80,80,0.08)",
    border: "1px solid rgba(255,120,120,0.25)",
    color: "#ffb3b3",
  },
};
